import { useNavigator } from '@repo/toolkit/use-navigator'
import Box from '@repo/ui/box'
import Button from '@repo/ui/button'
import { CalendarSevenIcon, DuplicateIcon } from '@repo/ui/icons'
import Lock from '@repo/ui/lock'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useRef, useState } from 'react'
import { Fragment } from 'react/jsx-runtime'
import { useParams } from 'react-router-dom'
import { toast } from 'sonner'

import { duplicateTripBooking } from '../../api/trip-bookings/duplicate-trip-booking'
import { getTripBooking } from '../../api/trip-bookings/get-trip-booking'
import {
  lockTripBooking,
  unlockTripBooking,
  updateTripBooking
} from '../../api/trip-bookings/update-trip-booking'
import { ItemNotFound } from '../../components/elements/item-not-found'
import LoadingScreen from '../../components/elements/loading/loading-screen'
import UserNavBar from '../../components/elements/nav-bar'
import TripBookingDocumentsDataTable from '../../components/pages/trip-bookings/trip-booking-documents-data-table'
import TripBookingForm from '../../components/pages/trip-bookings/trip-booking-form'
import { TripBookingLockedNotificationModal } from '../../components/pages/trip-bookings/trip-booking-locked-notification-modal'
import TripBookingPaymentsDataTable from '../../components/pages/trip-bookings/trip-booking-payments-data-table'
import TripBookingRemindersDataTable from '../../components/pages/trip-bookings/trip-booking-reminders-data-table'
import TripBookingTripSteps from '../../components/pages/trip-bookings/trip-booking-trip-steps'
import { mapTripBookingToInput } from '../../components/pages/trip-bookings/utils'
import { Paths } from '../../lib/constants/path'
import { useObserver } from '../../lib/contexts/observer'
import { useModalManager } from '../../lib/hooks/use-modal-manager'
import { TransportTypeEnum } from '../../lib/types/enums/trip/trip-type.enum'
import { TripStepTypeEnum } from '../../lib/types/enums/trip-bookings/trip-step-type.enum'
import { TripBookingTypes } from '../../lib/types/models/trip-booking/trip-booking'
import { updateButtonsConfig } from '../../lib/utils/buttons-config/validation-buttons-config'
import { showAxiosError } from '../../lib/utils/functions/misc.functions'

const UpdateTripBookingPage = () => {
  const { id } = useParams<{ id: string }>()
  const { navigate, back } = useNavigator()
  const queryClient = useQueryClient()
  const paymentObserver = useObserver({ key: 'trip-booking-update' })
  const [locked, setLocked] = useState(false)
  const hasChangesRef = useRef(false)
  const { modalState, toggleModal, closeModal } = useModalManager([
    'update-trip-booking-notification'
  ])

  const [redirect, setRedirect] = useState<'return-to-list' | 'create-new' | null>(null)

  if (!id || isNaN(+id)) return <ItemNotFound title="Identifiant de réservation invalide" />

  const { mutateAsync } = useMutation({
    mutationFn: updateTripBooking,
    onError: showAxiosError,
    onSuccess() {
      queryClient.invalidateQueries({ exact: true, queryKey: ['trip-booking', id] })
      paymentObserver.notify(['trip-booking-payments'])
      toast.success('Réservation modifiée avec succès')
    }
  })

  const { mutate: lockTripBookingMutation } = useMutation({
    mutationFn: lockTripBooking,
    onSuccess: () => {
      setLocked(true)
      toast.success('Réservation verrouillée')
    },
    onError: () => {
      setLocked(false)
    }
  })

  const { mutate: unlockTripBookingMutation } = useMutation({
    mutationFn: unlockTripBooking,
    onSuccess: () => {
      setLocked(false)
      toast.success('Réservation déverrouillée')
    },
    onError: () => {
      setLocked(true)
    }
  })

  const { data, isLoading } = useQuery({
    queryKey: ['trip-booking', id],
    queryFn: () => getTripBooking(+id, 'PARTIAL')
  })

  const { mutate } = useMutation({
    mutationFn: duplicateTripBooking,
    onError: showAxiosError,
    onSuccess: (duplicatedTripBooking) => {
      toast.success('Réservation dupliquée avec succès')
      navigate(Paths.TRIP_BOOKING_UPDATE_PAGE.replace(':id', duplicatedTripBooking.id.toString()))
    }
  })

  useEffect(() => {
    if (data) setLocked(data.locked)
  }, [data])

  if (!data && !isLoading)
    return <ItemNotFound title="La réservation que vous cherchez n'existe pas" />
  if (!data || isLoading) return <LoadingScreen />

  // The notification popup only opens when a locked booking was actually modified
  const saveTripBooking = async (values: TripBookingTypes.Input) => {
    const shouldNotify = locked && hasChangesRef.current
    await mutateAsync({ id: data.id, ...values })
    if (shouldNotify) toggleModal('update-trip-booking-notification')
    return shouldNotify
  }

  return (
    <div>
      <UserNavBar
        title={
          <div className={'flex items-center gap-2'}>
            {`Modifier la réservation ${!data.isTakeInCharge ? data.reservationNumber : ''}`}
            <Lock
              isLocked={locked}
              onClick={() => {
                if (locked) unlockTripBookingMutation(data.id)
                else lockTripBookingMutation(data.id)
              }}
            />
          </div>
        }
        icon={CalendarSevenIcon}
      />
      <div className={'h-4'} />
      <Box className={'flex flex-col gap-4'}>
        <div className={'flex justify-end items-center'}>
          <Button
            icon={DuplicateIcon}
            variant={'secondary'}
            label={'Dupliquer la réservation'}
            onClick={() => mutate(data.id)}
          />
        </div>

        <TripBookingForm
          key={data.id} // Add key to force re-render when data changes
          tripBookingId={data.id}
          defaultValues={mapTripBookingToInput(data)}
          onDirtyChange={(isDirty) => (hasChangesRef.current = isDirty)}
          buttonsConfig={updateButtonsConfig({
            onCancel: () => back(Paths.TRIP_BOOKING_LIST_PAGE),
            onUpdate: saveTripBooking,
            onUpdateThenCreate: async (values) => {
              const shouldNotify = await saveTripBooking(values)
              if (!shouldNotify) navigate(Paths.TRIP_BOOKING_CREATE_PAGE)
              else setRedirect('create-new')
            },
            onUpdateThenList: async (values) => {
              const shouldNotify = await saveTripBooking(values)
              if (!shouldNotify) back(Paths.TRIP_BOOKING_LIST_PAGE)
              else setRedirect('return-to-list')
            }
          })}
        />
      </Box>

      {!data.isTakeInCharge && (
        <>
          {(data.directionality === TransportTypeEnum.ONE_WAY ||
            data.directionality === TransportTypeEnum.ROUND_TRIP) && (
            <Fragment>
              <div className={'h-4'} />
              {!data.isTakeInCharge && (
                <TripBookingTripSteps
                  type={TripStepTypeEnum.INBOUND}
                  directionality={data.directionality}
                  tripBookingId={data.id}
                  transportMean={data.transportMeans}
                />
              )}
            </Fragment>
          )}

          {(data.directionality === TransportTypeEnum.RETURN ||
            data.directionality === TransportTypeEnum.ROUND_TRIP) && (
            <Fragment>
              <div className={'h-4'} />
              {!data.isTakeInCharge && (
                <TripBookingTripSteps
                  type={TripStepTypeEnum.OUTBOUND}
                  directionality={data.directionality}
                  tripBookingId={data.id}
                  transportMean={data.transportMeans}
                />
              )}
            </Fragment>
          )}

          <div className={'h-4'} />
          <TripBookingRemindersDataTable tripBookingId={data.id} />
          <div className={'h-4'} />
          <TripBookingDocumentsDataTable tripBookingId={data.id} />
          <div className={'h-4'} />
          <TripBookingPaymentsDataTable tripBookingId={data.id} />
        </>
      )}

      <TripBookingLockedNotificationModal
        tripBookingId={data.id}
        label="Vous avez modifié une réservation verrouillée. Souhaitez-vous envoyer une alerte ?"
        isOpen={modalState['update-trip-booking-notification']}
        closeModal={() => {
          if (redirect === 'create-new') navigate(Paths.TRIP_BOOKING_CREATE_PAGE)
          else if (redirect === 'return-to-list') back(Paths.TRIP_BOOKING_LIST_PAGE)
          closeModal('update-trip-booking-notification')
        }}
      />
    </div>
  )
}

export default UpdateTripBookingPage
